import Cookies from 'js-cookie'
import { useContext, useEffect, useState } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { ClientGuideDto, GuideTypes } from '@model/ClientGuideDto'

import { AuthSessionContext } from './AuthContext'
import { useProductReadContext } from './ProductContext'

const firstTimeGuideSessionName = 'fti_guide_session'

interface GuideHookData {
  guide?: ClientGuideDto
  fetchGuide: () => Promise<void>
  markGuideAsRead: () => Promise<void>
  isLoading: boolean
}

export const useGuide = (disabled?: boolean): GuideHookData => {
  const [auth] = useContext(AuthSessionContext)
  const [isLoading, setIsLoading] = useState(false)
  const [guide, setGuide] = useState<ClientGuideDto | undefined>(undefined)
  const { product } = useProductReadContext()
  const authSessionId = auth?.sessionId
  const apiClient = useApiClient(ClientApiClient)

  const fetchGuide = async () => {
    const guide = await apiClient.getGuideByTypeId(GuideTypes.IntroductionGuide, {
      tickmillProductId: product,
    })
    setGuide(guide)
  }

  const markGuideAsRead = async () => {
    if (!authSessionId) {
      return
    }
    const cookieName = `${firstTimeGuideSessionName}_${product}`

    const guideSessionId = Cookies.get(cookieName)
    if (authSessionId === guideSessionId) {
      return
    }
    Cookies.set(cookieName, authSessionId)
    await apiClient.updateClientGuideRead(GuideTypes.IntroductionGuide, {
      tickmillProductId: product,
    })
  }

  const handleGuide = async () => {
    try {
      setIsLoading(true)
      if (!authSessionId) {
        return
      }
      await markGuideAsRead()
      await fetchGuide()
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (disabled) {
      return
    }
    handleGuide()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSessionId])

  return {
    guide,
    fetchGuide,
    markGuideAsRead,
    isLoading,
  }
}
